import { GetDataParams, useTableData, SortDirection } from "./useTableData";
import { Table, TableProps } from "./Table";
import { useCallback, useMemo } from "react";
import { PaginatedResponse } from "./useTableData";
import { ColumnProps } from "./Table";

export interface StaticDataColumnProps extends Omit<ColumnProps<Record<string, any>>, "convertToCellData" | "sortKey"> {
  title: string;
  sortable: boolean;
  defaultSort?: boolean;
  defaultSortDirection?: SortDirection;
};

export interface StaticDataRow {
  name: string;
  value: any;
  element?: React.ReactNode;
}

interface StaticDataTableProps extends Omit<TableProps<Record<string, any>>, "columns" | "tableData"> {
  tableId: string;
  columns: StaticDataColumnProps[];
  rows: StaticDataRow[][];
  updateUrlParams?: boolean;
  trailingColumnKey?: string;
}

const sortRows = (rows: StaticDataRow[][], sortField: string | undefined, sortDirection: SortDirection | undefined) => {
  if (!sortField || !sortDirection) return rows;
  return rows.sort((a: StaticDataRow[], b: StaticDataRow[]) => {
    const aCell = a.find(cell => cell.name === sortField);
    const bCell = b.find(cell => cell.name === sortField);

    const aValue = aCell?.value;
    const bValue = bCell?.value;

    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    let comparison = 0;
    if (typeof aValue === "string" && typeof bValue === "string") {
        comparison = aValue.localeCompare(bValue);
    } else if (typeof aValue === "number" && typeof bValue === "number") {
        comparison = aValue - bValue;
    } else if (aValue instanceof Date && bValue instanceof Date) {
        comparison = aValue.getTime() - bValue.getTime();
    } else {
        comparison = String(aValue).localeCompare(String(bValue));
    }

    return sortDirection === SortDirection.ASC ? comparison : -comparison;
  });
};

const paginateRows = (rows: StaticDataRow[][], page: number | undefined, pageSize: number | undefined): PaginatedResponse<Record<string, any>> => {
  const currentPage = page || 1;
  const currentPageSize = pageSize || 10; // default is 10 items per page

  const convertedRows = rows.map(row =>
    row.reduce((acc, cell) => ({ ...acc, [cell.name]: cell.element || cell.value }), {})
  );

  const totalItems = convertedRows.length;
  const totalPages = Math.ceil(totalItems / currentPageSize);
  const startIndex = (currentPage - 1) * currentPageSize;
  const endIndex = startIndex + currentPageSize;

  return {
    items: convertedRows.slice(startIndex, endIndex),
    total: totalItems,
    pageCount: totalPages,
    hasNextPage: currentPage < totalPages,
  };
}

/**
 * This table is ONLY used if data is static.
 * Applies client-side Sorting + Pagination. Default page size is 10 items.
 * */
export const StaticDataTable = ({ tableId, columns, rows, updateUrlParams = true, trailingColumnKey, ...props }: StaticDataTableProps) => {
  const getData = useCallback(({ page, pageSize, sortProperty, sortDirection }: GetDataParams) => {
    const sortedRows = sortRows(rows, sortProperty, sortDirection);
    const paginatedRows = paginateRows(sortedRows, page, pageSize);
    return Promise.resolve(paginatedRows);
  }, [rows]);

  const defaultSortColumn = useMemo(() =>
    columns.find(col => col.defaultSort),
    [columns]
  );

  const tableData = useTableData<Record<string, any>>({
    getData,
    tableId,
    updateUrlParams,
    initialSortField: defaultSortColumn?.title,
    initialSortDirection: defaultSortColumn?.defaultSortDirection || SortDirection.ASC
  });

  const trueColumns = useMemo(() => {
    return columns.map(({ sortable, ...column }) => ({
      ...column,
      convertToCellData: (item: Record<string, any>) => item[column.title],
      sortKey: sortable? column.title : undefined,
    }));
  }, [columns]);

  return (
    <Table tableData={tableData} columns={trueColumns} trailingColumnKey={trailingColumnKey} {...props} />
  );
}
